'use client';

import { useEffect, useRef, useState } from 'react';

interface AnimationMeta {
  name: string;
  json: string;
  png: string;
  frames: number;
  frame_size: { w: number; h: number };
  sheet_size?: { w: number; h: number };
}

interface SpriteCanvasPlayerProps {
  id: string;
  folder: string;
  animations: Record<string, AnimationMeta>;
  defaultAnimation: string;
}

export default function SpriteCanvasPlayer({
  id,
  folder,
  animations,
  defaultAnimation,
}: SpriteCanvasPlayerProps) {
  const [activeAnimKey, setActiveAnimKey] = useState<string>(
    defaultAnimation || Object.keys(animations)[0]
  );
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [currentFrame, setCurrentFrame] = useState<number>(0);
  const [totalFrames, setTotalFrames] = useState<number>(16);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animTimerRef = useRef<number | null>(null);
  const loadedImageRef = useRef<HTMLImageElement | null>(null);
  const animationJsonRef = useRef<any>(null);

  const animKeys = Object.keys(animations);
  const activeAnim = animations[activeAnimKey] || animations[animKeys[0]];

  // Load Image and JSON when active animation changes
  useEffect(() => {
    if (!activeAnim) return;

    let cancelled = false;
    const basePath = `/free-library/${folder}`;
    const pngUrl = `${basePath}/${activeAnim.png}`;
    const jsonUrl = `${basePath}/${activeAnim.json}`;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = pngUrl;

    img.onload = () => {
      if (cancelled) return;
      loadedImageRef.current = img;

      fetch(jsonUrl)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
        .then((json) => {
          if (cancelled) return;
          animationJsonRef.current = json;
          const count = json?.frames ? Object.keys(json.frames).length : activeAnim.frames || 16;
          setTotalFrames(count);
          setCurrentFrame(0);
          drawFrame(0, img, json);
        });
    };

    return () => {
      cancelled = true;
    };
  }, [activeAnimKey, folder, activeAnim]);

  // Frame Draw helper
  const drawFrame = (frameIdx: number, img: HTMLImageElement | null, json: any) => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.imageSmoothingEnabled = false;

    let srcX = 0;
    let srcY = 0;
    let srcW = img.width;
    let srcH = img.height;

    if (json?.frames && json.frames[String(frameIdx)]) {
      const f = json.frames[String(frameIdx)];
      srcX = f.x;
      srcY = f.y;
      srcW = f.w;
      srcH = f.h;
    } else {
      const fw = activeAnim?.frame_size?.w || 256;
      const fh = activeAnim?.frame_size?.h || 256;
      const cols = Math.floor(img.width / fw) || 4;
      srcX = (frameIdx % cols) * fw;
      srcY = Math.floor(frameIdx / cols) * fh;
      srcW = fw;
      srcH = fh;
    }

    canvas.width = 384;
    canvas.height = 384;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const scale = (canvas.width / Math.max(srcW, srcH)) * 0.88;
    const dw = srcW * scale;
    const dh = srcH * scale;
    const dx = (canvas.width - dw) / 2;
    const dy = (canvas.height - dh) / 2;

    ctx.drawImage(img, srcX, srcY, srcW, srcH, dx, dy, dw, dh);
  };

  // Animation Loop
  useEffect(() => {
    if (!isPlaying) {
      if (animTimerRef.current) {
        cancelAnimationFrame(animTimerRef.current);
        animTimerRef.current = null;
      }
      return;
    }

    let lastTime = performance.now();
    const interval = 1000 / 12; // 12 FPS

    const loop = (now: number) => {
      const elapsed = now - lastTime;
      if (elapsed >= interval) {
        lastTime = now - (elapsed % interval);
        setCurrentFrame((prev) => {
          const next = totalFrames > 0 ? (prev + 1) % totalFrames : 0;
          drawFrame(next, loadedImageRef.current, animationJsonRef.current);
          return next;
        });
      }
      animTimerRef.current = requestAnimationFrame(loop);
    };

    animTimerRef.current = requestAnimationFrame(loop);

    return () => {
      if (animTimerRef.current) {
        cancelAnimationFrame(animTimerRef.current);
        animTimerRef.current = null;
      }
    };
  }, [isPlaying, totalFrames]);

  // Handle Scrub
  const handleScrub = (val: number) => {
    setIsPlaying(false);
    setCurrentFrame(val);
    drawFrame(val, loadedImageRef.current, animationJsonRef.current);
  };

  const fw = activeAnim?.frame_size?.w || 256;
  const fh = activeAnim?.frame_size?.h || 256;
  const cols = Math.round(Math.sqrt(totalFrames)) || 4;
  const rows = Math.ceil(totalFrames / cols) || 4;

  const framePadded = String(currentFrame + 1).padStart(2, '0');
  const totalPadded = String(totalFrames).padStart(2, '0');

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Big Checkerboard Tile */}
      <div
        className="aspect-square flex items-center justify-center overflow-hidden border border-border rounded-md bg-[#151a20]"
        style={{
          width: '100%',
          backgroundImage: 'conic-gradient(#20262e 25%, transparent 0 50%, #20262e 0 75%, transparent 0)',
          backgroundSize: '20px 20px',
        }}
      >
        <canvas
          ref={canvasRef}
          width={384}
          height={384}
          style={{
            width: '82%',
            height: '82%',
            objectFit: 'contain',
            imageRendering: 'pixelated',
          }}
        />
      </div>

      {/* Action Switcher Buttons */}
      {animKeys.length > 1 && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
          {animKeys.map((k) => {
            const label = k.replace(`${id}-`, '').replace(id, 'Action');
            const isActive = k === activeAnimKey;
            return (
              <button
                key={k}
                onClick={() => setActiveAnimKey(k)}
                style={{
                  background: isActive ? 'var(--primary)' : 'var(--surface-card)',
                  color: isActive ? 'var(--on-primary)' : 'var(--muted-strong)',
                  border: isActive ? '1px solid var(--primary)' : '1px solid var(--hairline-border)',
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '4px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  transition: 'all 0.15s ease',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Controls Bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px' }}>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          type="button"
          aria-label={isPlaying ? 'Pause animation' : 'Play animation'}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '6px',
            background: 'var(--surface-elevated)',
            border: '1px solid var(--border-strong)',
            color: 'var(--body-text)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            flexShrink: 0,
          }}
        >
          {isPlaying ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="14" y="4" width="4" height="16" rx="1"></rect>
              <rect x="6" y="4" width="4" height="16" rx="1"></rect>
            </svg>
          ) : (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          )}
        </button>

        <input
          type="range"
          className="w-full accent-primary h-1.5 bg-secondary rounded cursor-pointer"
          min={0}
          max={Math.max(0, totalFrames - 1)}
          value={currentFrame}
          onChange={(e) => handleScrub(parseInt(e.target.value, 10))}
          style={{ flex: 1 }}
          aria-label="Scrub frames"
        />

        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '12px',
          color: 'var(--muted)',
          width: '60px',
          textAlign: 'right',
          flexShrink: 0,
        }}>
          {framePadded} / {totalPadded}
        </span>
      </div>

      <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '8px' }}>
        {fw}×{fh} per frame · {cols}×{rows} grid · transparent PNG
      </p>
    </div>
  );
}
